import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Section, SplitView } from '@vnetwork/ui/layout';
import { type BarListItem } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Skeleton } from '@vnetwork/ui/primitives';
import {
  useDomainListAll,
  useCentralSeries,
  useDomainUsage,
  CentralMetric,
  periodToRange,
  type CentralSeriesPoint,
  type AnalyticsPeriod,
  type Granularity,
} from '@/api/manticore';
import {
  useServiceCdnOverview,
  useServiceCdnAnalytics,
  useServiceCdnBusiness,
} from '@/api/manticore/service-cdn';
import { useCustomer } from '@/api/customer';
import { useCdnUmbrellas } from '@/api/manticore/cdn-providers';
import { CdnConsoleBreadcrumbs } from './cdn/CdnConsoleBreadcrumbs';
import { ManticoreOverviewRail } from './ManticoreOverviewRail';
import { ManticoreTrafficCards, type TrafficMetric } from './ManticoreTrafficCards';
import { ManticoreTopDomains } from './ManticoreTopDomains';
import { BarCard, Stack, CardGrid, PeriodControl, type PeriodOpt } from './_shared/AnalyticsKit';
import {
  formatBps,
  formatBytes,
  formatNum,
  formatPct,
  sumSeries,
  maxSeries,
  avgSeries,
  peakRateDayCaveat,
  fmtAxisTime,
  PERIOD_LABEL,
} from './_shared/analytics-format';

const SERVICE_PERIODS: PeriodOpt[] = [
  { value: '24h', label: '24H' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
];

/** "6 Jun — 7 Jun" window label for a range. */
function rangeLabel(range: { from: string; to: string }): string {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${fmt(range.from)} — ${fmt(range.to)}`;
}

/** Reduce a metric series to sparkline samples with a time label for tooltips. */
const sparkData = (pts: CentralSeriesPoint[] | undefined, granularity?: Granularity) =>
  (pts ?? []).map((p) => ({ t: fmtAxisTime(p.timestamp, granularity ?? '1h'), v: Number(p.value) || 0 }));

function mapToBars(
  map: Record<string, CentralSeriesPoint[]> | undefined,
  display: (n: number) => string = formatNum,
  max = 6,
): BarListItem[] {
  return Object.entries(map ?? {})
    .map(([label, pts]) => ({ label, value: sumSeries(pts), display: display(sumSeries(pts)) }))
    .filter((b) => b.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, max);
}

/**
 * Manticore (Edge & CDN) dashboard — org-wide headline traffic over the last
 * 24h from the central analytics store, plus a jump-off to the domain list and
 * the deeper Analytics / Business Usage surfaces.
 *
 * When rendered under a `/services/cdn/:serviceId/*` route, all data fetches
 * are scoped to that service's endpoints. The JSX is identical in both modes.
 */
export default function ManticoreOverview() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId?: string }>();
  const isScoped = !!serviceId;

  // Org-wide domain list (used when not scoped).
  const allDomainsQ = useDomainListAll();

  const [period, setPeriod] = useState<AnalyticsPeriod>('24h');
  // periodToRange() reads `now`, so memoize per-period to keep the usage query
  // key stable (otherwise it refetches every render).
  const range = useMemo(() => periodToRange(period), [period]);
  const { from, to, granularity } = range;

  // ---- Org-wide analytics (used when not scoped) --------------------------
  const common = { domainId: 'all', period } as const;
  const orgReqQ = useCentralSeries({ ...common, metric: CentralMetric.Requests, enabled: !isScoped });
  const orgVolQ = useCentralSeries({ ...common, metric: CentralMetric.Bandwidth, enabled: !isScoped });
  const orgRateQ = useCentralSeries({ ...common, metric: CentralMetric.BandwidthRate, enabled: !isScoped });
  const orgHitQ = useCentralSeries({ ...common, metric: CentralMetric.ReqHitRatio, enabled: !isScoped });
  const orgGeoQ = useCentralSeries({ ...common, metric: CentralMetric.GeoRequests, breakdown: 'country', enabled: !isScoped });
  const orgProvQ = useCentralSeries({ ...common, metric: CentralMetric.Requests, groupBy: 'provider', enabled: !isScoped });
  const orgStatusQ = useCentralSeries({ ...common, metric: CentralMetric.Status, breakdown: 'code', enabled: !isScoped });
  const orgUsageQ = useDomainUsage(!isScoped ? range : null);

  // ---- Service-scoped analytics (used when serviceId is present) ----------
  const svcOverviewQ = useServiceCdnOverview(serviceId ?? '');
  const svcReqQ = useServiceCdnAnalytics(serviceId ?? '', { metric: CentralMetric.Requests, from, to, granularity });
  const svcVolQ = useServiceCdnAnalytics(serviceId ?? '', { metric: CentralMetric.Bandwidth, from, to, granularity });
  const svcRateQ = useServiceCdnAnalytics(serviceId ?? '', { metric: CentralMetric.BandwidthRate, from, to, granularity });
  const svcHitQ = useServiceCdnAnalytics(serviceId ?? '', { metric: CentralMetric.ReqHitRatio, from, to, granularity });
  const svcGeoQ = useServiceCdnAnalytics(serviceId ?? '', { metric: CentralMetric.GeoRequests, from, to, granularity, breakdown: 'country' });
  const svcProvQ = useServiceCdnAnalytics(serviceId ?? '', { metric: CentralMetric.Requests, from, to, granularity, groupBy: 'provider' });
  const svcStatusQ = useServiceCdnAnalytics(serviceId ?? '', { metric: CentralMetric.Status, from, to, granularity, breakdown: 'code' });
  const svcUsageQ = useServiceCdnBusiness(serviceId ?? '', isScoped ? { from, to, granularity } : null);

  // ---- Resolve active data source based on scope --------------------------
  const reqQ   = isScoped ? svcReqQ   : orgReqQ;
  const volQ   = isScoped ? svcVolQ   : orgVolQ;
  const rateQ  = isScoped ? svcRateQ  : orgRateQ;
  const hitQ   = isScoped ? svcHitQ   : orgHitQ;
  const geoQ   = isScoped ? svcGeoQ   : orgGeoQ;
  const provQ  = isScoped ? svcProvQ  : orgProvQ;
  const statusQ = isScoped ? svcStatusQ : orgStatusQ;
  const usageQ = isScoped ? svcUsageQ : orgUsageQ;

  // Adapt service-scoped usage shape (DomainUsageRow[]) to match the orgUsageQ
  // shape that ManticoreTopDomains expects (also DomainUsageRow[]).
  const usageData = isScoped ? (svcUsageQ.data ?? []) : (orgUsageQ.data ?? []);

  // ---- Domain counts (org-wide uses the full list; scoped uses overview) ---
  const domainsLoading = isScoped ? svcOverviewQ.isLoading : allDomainsQ.isLoading;
  const counts = useMemo(() => {
    if (isScoped) {
      const ov = svcOverviewQ.data;
      return { total: ov?.total ?? 0, active: ov?.active ?? 0, deploying: 0, pending: 0 };
    }
    const domains = allDomainsQ.data ?? [];
    const total = domains.length;
    const by = (s: string) => domains.filter((d) => String(d.status ?? '').toLowerCase() === s).length;
    return { total, active: by('active'), deploying: by('deploying'), pending: by('pending') };
  }, [isScoped, svcOverviewQ.data, allDomainsQ.data]);

  const customer = useCustomer();

  // Scoped console only: when the caller can see more than one CDN service,
  // make it explicit that domains/providers managed here belong to THIS
  // service — secondary stacks are opt-in per service, so e.g. a provider
  // enabled on another service won't appear here.
  const umbrellasQ = useCdnUmbrellas(undefined, { enabled: isScoped });
  const umbrellas = umbrellasQ.data?.umbrellas ?? [];
  const currentUmbrella = umbrellas.find((u) => u.service_id === serviceId);
  const otherServiceCount = umbrellas.filter((u) => u.service_id !== serviceId).length;

  const trafficLoading = reqQ.isLoading || volQ.isLoading || rateQ.isLoading || hitQ.isLoading;
  const countryBars = useMemo(() => mapToBars(geoQ.data?.by_dimension), [geoQ.data]);
  const providerBars = useMemo(() => mapToBars(provQ.data?.by_provider), [provQ.data]);

  // Reach summary for the rail — distinct served countries/providers + the
  // busiest region with its request share. Derived from the same geo/provider
  // breakdowns the bar cards use, so it costs no extra fetch.
  const reach = useMemo(() => {
    const countByValue = (map: Record<string, CentralSeriesPoint[]> | undefined) =>
      Object.values(map ?? {}).filter((pts) => sumSeries(pts) > 0).length;
    const total = countryBars.reduce((acc, b) => acc + b.value, 0);
    const top = countryBars[0];
    return {
      countries: countByValue(geoQ.data?.by_dimension),
      providers: countByValue(provQ.data?.by_provider),
      topRegion: top?.label != null ? String(top.label) : undefined,
      topRegionShare: top && total > 0 ? Math.round((top.value / total) * 100) : undefined,
    };
  }, [geoQ.data, provQ.data, countryBars]);

  const reachLoading = geoQ.isLoading || provQ.isLoading;

  // Edge health from the status-code breakdown: 5xx share of all responses.
  // This is the real "needs attention" signal, beyond domain rollout state.
  const errorRate = useMemo(() => {
    const map = statusQ.data?.by_dimension ?? {};
    let total = 0;
    let errors = 0;
    for (const [code, pts] of Object.entries(map)) {
      const s = sumSeries(pts);
      total += s;
      if (code.startsWith('5')) errors += s;
    }
    return total > 0 ? errors / total : 0;
  }, [statusQ.data]);

  const api = {
    endpoint: 'https://openapi.vnetwork.vn',
    accountId: customer.data?.customer_id != null ? String(customer.data.customer_id) : undefined,
  };

  const metrics: TrafficMetric[] = [
    { label: 'Total requests', value: formatNum(sumSeries(reqQ.data?.series)), data: sparkData(reqQ.data?.series, reqQ.data?.granularity), format: formatNum },
    { label: 'Volume', value: formatBytes(sumSeries(volQ.data?.series)), data: sparkData(volQ.data?.series, volQ.data?.granularity), format: formatBytes },
    { label: 'Peak bandwidth', value: formatBps(maxSeries(rateQ.data?.series)), data: sparkData(rateQ.data?.series, rateQ.data?.granularity), format: formatBps, hint: peakRateDayCaveat(rateQ.data?.granularity) || undefined },
    // No hit-ratio rows ≠ 0% hit ratio — vendors only emit the metric for
    // domains with cache activity, so an absent series renders as "no data".
    { label: 'Cache hit ratio', value: (hitQ.data?.series?.length ?? 0) > 0 ? formatPct(avgSeries(hitQ.data?.series), '0..1') : '—', data: sparkData(hitQ.data?.series, hitQ.data?.granularity), format: (v) => formatPct(v, '0..1') },
  ];

  return (
    <>
      <Container size="xl">
        <PageHeader
          icon={<Globe size={22} />}
          breadcrumbs={
            isScoped ? (
              <CdnConsoleBreadcrumbs />
            ) : (
              <Breadcrumbs>
                <BreadcrumbItem asChild>
                  <Link to="/services">Services</Link>
                </BreadcrumbItem>
              </Breadcrumbs>
            )
          }
          title={isScoped ? 'Overview' : 'Multi-CDN Orchestration'}
          description="Edge & CDN — caching, security, TLS and delivery across multiple CDN backends."
        />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {isScoped && otherServiceCount > 0 && (
          <Alert tone="info" title={`You have ${otherServiceCount + 1} CDN services`}>
            Domains, providers and settings on this console apply to
            {currentUmbrella ? ` “${currentUmbrella.service_name}”` : ' this service'} only.{' '}
            <button
              type="button"
              onClick={() => navigate('/services/cdn')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--vn-primary-text)',
                textDecoration: 'underline',
              }}
            >
              View all CDN services
            </button>
          </Alert>
      )}

      <SplitView
        asideWidth="320px"
        aside={
          domainsLoading ? (
            <Skeleton style={{ height: 320 }} />
          ) : (
            <ManticoreOverviewRail
              counts={counts}
              reach={reach}
              reachLoading={reachLoading}
              errorRate={errorRate}
              errorRateLoading={statusQ.isLoading}
              api={api}
              base={`/services/cdn/${serviceId}`}
            />
          )
        }
      >
        <Stack>
          <Section title="Traffic" description="Edge traffic across your domains." style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--vn-space-3)', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
              <PeriodControl options={SERVICE_PERIODS} value={period} onChange={setPeriod} />
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{rangeLabel(range)}</span>
            </div>
            <ManticoreTrafficCards items={metrics} loading={trafficLoading} />
          </Section>

          <CardGrid min={260}>
            <BarCard title="Top countries" description={`By request volume (${PERIOD_LABEL[period]}).`} items={countryBars} loading={geoQ.isLoading} />
            <BarCard title="Traffic by provider" description={`Request share per CDN (${PERIOD_LABEL[period]}).`} items={providerBars} loading={provQ.isLoading} />
          </CardGrid>

          <ManticoreTopDomains rows={usageData} loading={usageQ.isLoading} periodLabel={PERIOD_LABEL[period]} />
        </Stack>
      </SplitView>
      </div>
      </Container>
    </>
  );
}
